// RoomScan — actual UI + behaviour for the 360° room sweep.
//
// Differs from WebcamCapture (used in the IdentityGate) because:
//   * No user-triggered capture — frames are extracted AUTOMATICALLY at
//     a fixed cadence during the timed sweep.
//   * A visible countdown timer guides the candidate through the rotation.
//   * No mirroring on captured frames — Rekognition prefers un-mirrored
//     images, and object detection isn't sensitive to mirroring anyway.
//
// Component states:
//   'idle'      — instructions + Start button
//   'scanning'  — webcam live, timer counting down, frames being captured
//   'analyzing' — handed off to the parent (which is calling /finalize)
//
// Props:
//   minSweepSeconds : number  — duration of the scan (default 10)
//   captureEveryMs  : number  — frame cadence (default 1500ms)
//   onStart()       : callback when the candidate clicks "Start scan"
//                     (parent should POST /api/baseline/start here)
//   onFrame(blob, frameIndex) : called for each captured frame; returns Promise
//   onComplete(sweepSeconds)  : called when the scan timer ends — parent
//                                should call /api/baseline/finalize here
//   busy            : boolean — disables the Start button when parent is busy

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Camera, Loader2, AlertTriangle, RefreshCw, RotateCw,
} from 'lucide-react';

const JPEG_QUALITY = 0.85;          // smaller than identity selfies — object
                                     // detection tolerates more compression
const PREVIEW_WIDTH = 480;
const PREVIEW_HEIGHT = 360;
const DEFAULT_SWEEP_SECONDS = 10;
const DEFAULT_CAPTURE_EVERY_MS = 1500;


export default function RoomScan({
  minSweepSeconds = DEFAULT_SWEEP_SECONDS,
  captureEveryMs = DEFAULT_CAPTURE_EVERY_MS,
  onStart,
  onFrame,
  onComplete,
  busy = false,
}) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const captureTimerRef = useRef(null);
  const countdownTimerRef = useRef(null);
  const startedAtRef = useRef(0);
  const frameCounterRef = useRef(0);

  const [phase, setPhase] = useState('idle');         // idle | scanning | analyzing
  const [error, setError] = useState(null);
  const [framesCapture, setFramesCaptured] = useState(0);
  const [secondsLeft, setSecondsLeft] = useState(minSweepSeconds);
  const [streamReady, setStreamReady] = useState(false);

  // ── Open the webcam on mount; close on unmount ──────────────────────────
  useEffect(() => {
    let cancelled = false;
    setError(null);

    navigator.mediaDevices.getUserMedia({
      video: {
        facingMode: { ideal: 'user' },
        // 2× preview resolution so frames sent to Rekognition are crisp.
        width:  { ideal: PREVIEW_WIDTH  * 2 },
        height: { ideal: PREVIEW_HEIGHT * 2 },
      },
      audio: false,
    }).then((stream) => {
      if (cancelled) {
        stream.getTracks().forEach((t) => t.stop());
        return;
      }
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play().catch(() => { /* autoplay blocked, will retry */ });
      }
      setStreamReady(true);
    }).catch((err) => {
      if (cancelled) return;
      let friendly = err?.message || String(err);
      if (err?.name === 'NotAllowedError') {
        friendly = 'Camera access denied. Please allow camera permissions and refresh.';
      } else if (err?.name === 'NotFoundError') {
        friendly = 'No camera detected on this device.';
      }
      setError(friendly);
    });

    return () => {
      cancelled = true;
      if (captureTimerRef.current) clearInterval(captureTimerRef.current);
      if (countdownTimerRef.current) clearInterval(countdownTimerRef.current);
      const s = streamRef.current;
      if (s) s.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    };
  }, []);

  // ── Capture one frame from the live video → upload via onFrame ──────────
  const captureFrame = useCallback(() => {
    if (!videoRef.current || !streamRef.current) return;
    const video = videoRef.current;
    if (video.readyState < 2) return;   // metadata not loaded yet

    const w = video.videoWidth || PREVIEW_WIDTH;
    const h = video.videoHeight || PREVIEW_HEIGHT;
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Do NOT mirror — Rekognition works on the natural orientation.
    ctx.drawImage(video, 0, 0, w, h);

    const frameIndex = frameCounterRef.current;
    frameCounterRef.current += 1;

    canvas.toBlob((blob) => {
      if (!blob) return;
      setFramesCaptured((n) => n + 1);
      // Fire-and-forget so a slow upload doesn't block the next capture.
      void (async () => {
        try {
          await onFrame?.(blob, frameIndex);
        } catch (_) { /* parent handles its own errors */ }
      })();
    }, 'image/jpeg', JPEG_QUALITY);
  }, [onFrame]);

  // ── Run the scan: capture-every-Nms + countdown timer ──────────────────
  const handleStart = async () => {
    if (!streamReady || phase !== 'idle') return;
    setError(null);
    frameCounterRef.current = 0;
    setFramesCaptured(0);

    // Tell the parent to initialise the server row BEFORE we start
    // uploading frames. If this fails, abort.
    if (typeof onStart === 'function') {
      const startRes = await onStart();
      if (startRes && startRes.ok === false) {
        setError(startRes.error || 'Could not start scan on the server');
        return;
      }
    }

    setPhase('scanning');
    startedAtRef.current = Date.now();
    setSecondsLeft(minSweepSeconds);

    // First frame immediately so the candidate sees progress straight away.
    captureFrame();

    captureTimerRef.current = setInterval(captureFrame, captureEveryMs);

    countdownTimerRef.current = setInterval(() => {
      const elapsed = (Date.now() - startedAtRef.current) / 1000;
      const remaining = Math.max(0, minSweepSeconds - elapsed);
      setSecondsLeft(remaining);
      if (remaining <= 0) {
        // Stop captures + countdown.
        if (captureTimerRef.current) clearInterval(captureTimerRef.current);
        if (countdownTimerRef.current) clearInterval(countdownTimerRef.current);
        captureTimerRef.current = null;
        countdownTimerRef.current = null;
        setPhase('analyzing');
        const sweepSeconds = (Date.now() - startedAtRef.current) / 1000;
        // Hand off to parent so it can call /finalize. We don't await
        // because the parent owns the verdict state.
        void onComplete?.(sweepSeconds);
      }
    }, 250);
  };

  // ── Render ──────────────────────────────────────────────────────────────
  const progressPct = phase === 'idle'
    ? 0
    : Math.min(100, ((minSweepSeconds - secondsLeft) / minSweepSeconds) * 100);

  return (
    <div className="space-y-4">
      <Header phase={phase} />

      {/* Live webcam preview */}
      <div
        className="relative mx-auto rounded-lg overflow-hidden bg-black"
        style={{ width: `${PREVIEW_WIDTH}px`, height: `${PREVIEW_HEIGHT}px` }}
      >
        {error ? (
          <div className="w-full h-full flex flex-col items-center justify-center text-white p-4 text-center">
            <AlertTriangle className="w-8 h-8 text-red-400 mb-2" />
            <div className="text-sm">{error}</div>
          </div>
        ) : (
          <video
            ref={videoRef}
            autoPlay muted playsInline
            style={{
              width: '100%', height: '100%', objectFit: 'cover',
              transform: 'scaleX(-1)',  // mirror for the candidate's preview
            }}
          />
        )}

        {/* Phase overlays */}
        {phase === 'scanning' && (
          <div className="absolute top-2 right-2 bg-red-600 text-white text-xs font-bold px-2 py-0.5 rounded animate-pulse">
            ● SCANNING
          </div>
        )}
        {phase === 'scanning' && (
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 bg-black/70 text-white text-xl font-bold px-4 py-1 rounded-full">
            {Math.ceil(secondsLeft)}s
          </div>
        )}
        {phase === 'analyzing' && (
          <div className="absolute inset-0 bg-black/70 flex flex-col items-center justify-center text-white">
            <Loader2 className="w-10 h-10 animate-spin mb-2" />
            <div className="text-sm font-semibold">Analysing your workspace…</div>
            <div className="text-xs mt-1 opacity-80">
              Reading frames through AI — takes ~10 seconds
            </div>
          </div>
        )}
      </div>

      {/* Progress bar for the scan */}
      {phase === 'scanning' && (
        <div className="space-y-1">
          <div className="h-2 bg-gray-200 rounded overflow-hidden">
            <div
              className="h-full bg-blue-600 transition-all duration-200"
              style={{ width: `${progressPct}%` }}
            />
          </div>
          <div className="text-xs text-gray-600 text-center">
            {framesCapture} frame{framesCapture === 1 ? '' : 's'} captured
          </div>
        </div>
      )}

      {/* Idle controls */}
      {phase === 'idle' && (
        <div className="space-y-3">
          <Instructions />
          <button
            type="button"
            onClick={handleStart}
            disabled={!streamReady || busy || !!error}
            className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-md text-sm font-medium"
          >
            <RotateCw className="w-4 h-4 mr-2" />
            Start 360° scan
          </button>
        </div>
      )}
    </div>
  );
}


function Header({ phase }) {
  if (phase === 'scanning') {
    return (
      <div>
        <h3 className="text-lg font-semibold">Slowly rotate your camera 360°</h3>
        <p className="text-sm text-gray-600 mt-1">
          Show your entire workspace — desk, walls, doors, anywhere a phone
          or notes could be hidden. We'll catalogue what's normally present.
        </p>
      </div>
    );
  }
  if (phase === 'analyzing') {
    return (
      <div>
        <h3 className="text-lg font-semibold">Analysing your workspace</h3>
        <p className="text-sm text-gray-600 mt-1">
          We're running AI on the captured frames. This usually takes 10–15 seconds.
        </p>
      </div>
    );
  }
  return (
    <div>
      <h3 className="text-lg font-semibold">Scan your workspace</h3>
      <p className="text-sm text-gray-600 mt-1">
        Before the test starts, we need a quick 360° view of your room.
        This helps us tell the difference between things that are normally
        there vs items that appear during the test.
      </p>
    </div>
  );
}


function Instructions() {
  return (
    <div className="bg-blue-50 border border-blue-200 rounded p-3 space-y-2 text-sm">
      <div className="font-semibold text-blue-900">Tips for a good scan:</div>
      <ul className="list-disc pl-5 space-y-1 text-blue-900">
        <li>Slowly turn your laptop or webcam in a full circle.</li>
        <li>Show your desk surface, walls, and any nearby surfaces.</li>
        <li>Make sure the room is well-lit — dim frames don't catalog well.</li>
        <li>If you have a phone or notes on the desk, that's OK — we'll
          catalogue them as "normal for this candidate".</li>
      </ul>
    </div>
  );
}
